import { DotsThreeVertical } from '@phosphor-icons/react';
import { IconButton } from '@clay-run/glaze/iconbutton';
import { Icon } from '@clay-run/glaze/icon';
import type { IconType } from '@clay-run/glaze/icon';
import { Dropdown, DropdownButtonUnstyled, DropdownItem, DropdownMenu } from '@clay-run/glaze/dropdown/index';

/**
 * Shared classic 3-dot kebab menu (plan U3, Key Technical Decision 6). Also
 * reused by U5's waterfall rung cards — kept generic (a plain item list, no
 * config-page-specific typing) so that unit doesn't have to reshape it.
 *
 * ── COMPOSITION, VERIFIED AGAINST SOURCE FIRST ───────────────────────────
 * This is the FIRST usage of Glaze's `Dropdown` family anywhere in the repo
 * (verified: zero prior `@clay-run/glaze/dropdown` imports under `web/` or
 * `mini-apps/`), so every prop here was checked against
 * `node_modules/@clay-run/glaze/src/dropdown/dropdown.tsx` rather than
 * guessed from the prototype's `overflowMenu()` (~line 6159 of the v6
 * prototype — visual reference only, per plan Key Decision 6):
 *
 *  - Import path: `@clay-run/glaze/dropdown` does NOT resolve. Its `dist/`
 *    output is a directory (`dist/dropdown/`), not a flat
 *    `dist/dropdown.js` file, so the package's generic `"./*": "./dist/*.js"`
 *    export map 404s on the bare subpath — verified with a direct
 *    `require.resolve` against this worktree's `node_modules`. Same shape as
 *    `PlaybooksLayout.tsx`'s documented `@clay-run/glaze/toast` gotcha.
 *    `@clay-run/glaze/dropdown/index` is the path that actually resolves.
 *  - Trigger composition: `DropdownButtonUnstyled` renders a real `<button>`
 *    (`@base-ui/react`'s `Button` primitive) by default, so wrapping an
 *    `IconButton` (itself a `<button>`) as a CHILD would nest two buttons —
 *    invalid HTML and a false a11y tree. `DropdownButtonUnstyled` accepts a
 *    `render` prop (`BaseUIComponentProps`'s polymorphic-render mechanism,
 *    `node_modules/@base-ui/react/internals/types.d.ts`), which merges the
 *    dropdown's floating-ui reference props onto a single supplied element
 *    instead — verified against `Filter`'s own use of `Dropdown` in
 *    `filter.tsx`, the one real in-package consumer of this family. Passing
 *    `render={<IconButton .../>}` is therefore the only way to get one real
 *    button, not two.
 *  - Item styling: `DropdownItem`'s `variant="danger"` maps to the shared
 *    `menuItem` tailwind-variants config (`internal/shared-menu/menu-styles.ts`)
 *    — real destructive-item styling, not a guessed class.
 */

export interface KebabMenuItem {
  /** Stable key; also the semantic action id (used by tests, not rendered). */
  key: string;
  label: string;
  icon?: IconType;
  /** Renders in Glaze's destructive/danger item styling (e.g. Delete). */
  danger?: boolean;
  onSelect: () => void;
}

interface KebabMenuProps {
  items: KebabMenuItem[];
  /** Accessible name for the trigger — override per row (e.g. "More actions for {name}"). */
  ariaLabel?: string;
}

/**
 * Renders nothing when `items` is empty — a row whose derived item list is
 * empty (e.g. every action gated away) gets no dead trigger with an empty
 * popup.
 */
export function KebabMenu({ items, ariaLabel = 'More actions' }: KebabMenuProps) {
  if (items.length === 0) return null;

  return (
    <Dropdown placement="bottom-end">
      <DropdownButtonUnstyled
        render={<IconButton icon={DotsThreeVertical} variant="ghost" size="sm" aria-label={ariaLabel} />}
      >
        {/*
          `DropdownButtonUnstyledProps` requires `children: ReactNode` even
          when `render` is supplied (verified against
          `node_modules/@clay-run/glaze/src/dropdown/dropdown.tsx` — the type
          is a plain intersection, not a discriminated union keyed on
          `render`). base-ui's `render`-prop polymorphism replaces the
          rendered OUTPUT entirely with the supplied element (with its props
          merged on), so this `children` value is never actually shown —
          `null` satisfies the type without rendering a duplicate label.
        */}
        {null}
      </DropdownButtonUnstyled>
      <DropdownMenu>
        {items.map((item) => (
          <DropdownItem key={item.key} variant={item.danger ? 'danger' : 'standard'} onClick={item.onSelect}>
            {item.icon && <Icon type={item.icon} />}
            {item.label}
          </DropdownItem>
        ))}
      </DropdownMenu>
    </Dropdown>
  );
}
